import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import type { BrakingLapEntry } from '@/types/telemetry';
import { useI18n } from '@/context/I18nContext';

interface GGDiagramProps {
  activeLap: BrakingLapEntry | null;
  comparisonLap?: BrakingLapEntry | null;
  allLaps?: BrakingLapEntry[];
  hoveredSampleIndex?: number | null;
  onHoverSample?: (index: number | null) => void;
  height?: number;
}

export function GGDiagram({
  activeLap,
  comparisonLap,
  allLaps = [],
  hoveredSampleIndex,
  onHoverSample,
  height = 360,
}: GGDiagramProps) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [internalHoverIdx, setInternalHoverIdx] = useState<number | null>(null);

  const effectiveHoverIdx = hoveredSampleIndex !== undefined ? hoveredSampleIndex : internalHoverIdx;

  // Read G-force values from backend-enriched active lap samples
  const activeData = useMemo(() => {
    if (!activeLap?.samples || activeLap.samples.length === 0) return [];
    return activeLap.samples.map((s, idx) => {
      const gLat = s.gLat ?? 0;
      const gLong = s.gLong ?? 0;
      const speed = s.speedKmh ?? 0;
      const totalG = s.gTotal ?? 0;
      return { idx, gLat, gLong, speed, totalG, sample: s };
    });
  }, [activeLap]);

  // Read G-force values from backend-enriched comparison lap samples
  const compData = useMemo(() => {
    if (!comparisonLap?.samples || comparisonLap.samples.length === 0) return [];
    return comparisonLap.samples.map((s, idx) => {
      const gLat = s.gLat ?? 0;
      const gLong = s.gLong ?? 0;
      const speed = s.speedKmh ?? 0;
      const totalG = s.gTotal ?? 0;
      return { idx, gLat, gLong, speed, totalG, sample: s };
    });
  }, [comparisonLap]);

  // Ghost scatter points from other laps
  const ghostPoints = useMemo(() => {
    const pts: Array<{ gLat: number; gLong: number }> = [];
    for (const lap of allLaps) {
      if (lap === activeLap || lap === comparisonLap || !lap.samples) continue;
      for (const s of lap.samples) {
        pts.push({
          gLat: s.gLat ?? 0,
          gLong: s.gLong ?? 0,
        });
      }
    }
    return pts;
  }, [allLaps, activeLap, comparisonLap]);

  // Dynamic scale limit (default at least 2.0G, snapped to 0.5G increments)
  const maxG = useMemo(() => {
    let peak = 1.8;
    for (const pt of activeData) {
      peak = Math.max(peak, Math.abs(pt.gLat), Math.abs(pt.gLong));
    }
    for (const pt of compData) {
      peak = Math.max(peak, Math.abs(pt.gLat), Math.abs(pt.gLong));
    }
    return Math.min(3.0, Math.ceil((peak + 0.2) * 2) / 2);
  }, [activeData, compData]);

  // Render Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.resetTransform();
    ctx.scale(dpr, dpr);

    // Center coordinates
    const cx = w / 2;
    const cy = h / 2;
    const radius = Math.min(cx, cy) - 24;
    const scale = radius / maxG; // pixels per G

    // Helper: Map (gLat, gLong) to canvas pixels
    // gLat: >0 right, <0 left
    // gLong: >0 accel (up), <0 brake (down)
    const toCanvasX = (gLat: number) => cx + gLat * scale;
    const toCanvasY = (gLong: number) => cy - gLong * scale;

    ctx.clearRect(0, 0, w, h);

    // 1. Background concentric rings (0.5G increments)
    ctx.save();
    for (let g = 0.5; g <= maxG; g += 0.5) {
      const r = g * scale;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = g === 1.0 || g === 2.0 ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)';
      ctx.lineWidth = g === 1.0 || g === 2.0 ? 1.2 : 0.8;
      if (g % 1 !== 0) {
        ctx.setLineDash([3, 4]);
      } else {
        ctx.setLineDash([]);
      }
      ctx.stroke();

      // Ring label on top-right diagonal
      ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.font = '9px monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      const labelX = cx + r * Math.cos(Math.PI / 4) + 2;
      const labelY = cy - r * Math.sin(Math.PI / 4) - 2;
      ctx.fillText(`${g.toFixed(1)}G`, labelX, labelY);
    }
    ctx.restore();

    // 2. Crosshair axes
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx - radius - 8, cy);
    ctx.lineTo(cx + radius + 8, cy);
    ctx.moveTo(cx, cy - radius - 8);
    ctx.lineTo(cx, cy + radius + 8);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Subtle axis labels
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Accel (+G)', cx, cy - radius - 12);
    ctx.fillText('Braking (-G)', cx, cy + radius + 18);
    ctx.textAlign = 'left';
    ctx.fillText('Right', cx + radius + 12, cy + 3);
    ctx.textAlign = 'right';
    ctx.fillText('Left', cx - radius - 12, cy + 3);
    ctx.restore();

    // 3. Ghost scatter points from other laps
    if (ghostPoints.length > 0) {
      ctx.save();
      ctx.fillStyle = 'rgba(148, 163, 184, 0.12)';
      for (const pt of ghostPoints) {
        const px = toCanvasX(pt.gLat);
        const py = toCanvasY(pt.gLong);
        ctx.fillRect(px - 1, py - 1, 2, 2);
      }
      ctx.restore();
    }

    // 4. Comparison Lap (if active)
    if (compData.length > 1) {
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = '#a855f7';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      compData.forEach((pt, i) => {
        const px = toCanvasX(pt.gLat);
        const py = toCanvasY(pt.gLong);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.stroke();

      // Comparison points
      ctx.fillStyle = '#c084fc';
      for (const pt of compData) {
        const px = toCanvasX(pt.gLat);
        const py = toCanvasY(pt.gLong);
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    // 5. Active Lap Trajectory
    if (activeData.length > 1) {
      ctx.save();
      const n = activeData.length;

      // Draw multi-colored segment path showing braking progression
      for (let i = 0; i < n - 1; i++) {
        const p0 = activeData[i];
        const p1 = activeData[i + 1];
        const progress = i / (n - 1); // 0 (start brake) -> 1 (exit)

        ctx.beginPath();
        ctx.moveTo(toCanvasX(p0.gLat), toCanvasY(p0.gLong));
        ctx.lineTo(toCanvasX(p1.gLat), toCanvasY(p1.gLong));

        // Color coding by phase progression:
        const bStart = activeLap?.brakeStartIndex ?? 0;
        if (bStart > 0 && i < bStart) {
          ctx.strokeStyle = '#94a3b8'; // Approach straight before braking
        } else if (progress < 0.35) {
          ctx.strokeStyle = '#ef4444'; // Heavy initial straight-line braking
        } else if (progress < 0.65) {
          ctx.strokeStyle = '#f59e0b'; // Trail-braking turn-in transition
        } else if (progress < 0.85) {
          ctx.strokeStyle = '#06b6d4'; // Minimum corner speed
        } else {
          ctx.strokeStyle = '#10b981'; // Corner exit acceleration
        }

        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
      }

      // Sample dots along the path
      for (let i = 0; i < n; i++) {
        const pt = activeData[i];
        const px = toCanvasX(pt.gLat);
        const py = toCanvasY(pt.gLong);
        const isHovered = effectiveHoverIdx === i;

        ctx.beginPath();
        ctx.arc(px, py, isHovered ? 6 : 3, 0, Math.PI * 2);
        ctx.fillStyle = isHovered ? '#ffffff' : '#f8fafc';
        ctx.fill();
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // 6. Draw glowing reticle on hovered point
      if (effectiveHoverIdx != null && effectiveHoverIdx >= 0 && effectiveHoverIdx < n) {
        const target = activeData[effectiveHoverIdx];
        const hx = toCanvasX(target.gLat);
        const hy = toCanvasY(target.gLong);

        // Glow ring
        ctx.beginPath();
        ctx.arc(hx, hy, 10, 0, Math.PI * 2);
        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(hx, hy, 16, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(56, 189, 248, 0.4)';
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 2]);
        ctx.stroke();
      }

      ctx.restore();
    }
  }, [activeData, compData, ghostPoints, maxG, effectiveHoverIdx]);

  // Handle Mouse Move for Hover Scrubbing
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || activeData.length === 0) return;

    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const radius = Math.min(cx, cy) - 24;
    const scale = radius / maxG;

    // Find closest sample to mouse position
    let bestDist = Infinity;
    let bestIdx = -1;

    activeData.forEach((pt, i) => {
      const px = cx + pt.gLat * scale;
      const py = cy - pt.gLong * scale;
      const d = Math.hypot(mx - px, my - py);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    });

    if (bestDist < 40 && bestIdx !== -1) {
      setInternalHoverIdx(bestIdx);
      onHoverSample?.(bestIdx);
    } else {
      setInternalHoverIdx(null);
      onHoverSample?.(null);
    }
  }, [activeData, maxG, onHoverSample]);

  const handleMouseLeave = useCallback(() => {
    setInternalHoverIdx(null);
    onHoverSample?.(null);
  }, [onHoverSample]);

  const hoveredSample = effectiveHoverIdx != null && activeData[effectiveHoverIdx] ? activeData[effectiveHoverIdx] : null;

  return (
    <div ref={containerRef} className="card p-4 flex flex-col items-center relative overflow-hidden">
      {/* Header */}
      <div className="w-full flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-text-primary">
            {t('braking.ggDiagram')}
          </span>
          <span className="text-[10px] text-text-muted">
            ({t('braking.frictionCircle')})
          </span>
        </div>

        {/* Trail braking badge if active */}
        {activeLap && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-text-muted">
              {t('braking.trailScore')}:
            </span>
            <span className={`text-xs font-bold font-mono px-2 py-0.5 rounded-full ${
              activeLap.trailBrakingScore >= 80 ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' :
              activeLap.trailBrakingScore >= 65 ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
              'bg-red-500/15 text-red-400 border border-red-500/30'
            }`}>
              {activeLap.trailBrakingScore.toFixed(0)}%
            </span>
          </div>
        )}
      </div>

      {/* Canvas */}
      <div className="relative w-full flex justify-center items-center">
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="cursor-crosshair max-w-full"
          style={{ height: `${height}px`, width: '100%' }}
        />

        {/* Live Hover Info Floating Overlay */}
        {hoveredSample && (
          <div className="absolute top-2 left-2 pointer-events-none bg-bg-surface/90 backdrop-blur-md border border-border-subtle/80 rounded-lg p-2.5 shadow-xl text-xs space-y-1 font-mono">
            <div className="text-[10px] uppercase font-bold text-text-muted">
              {t('braking.sample')} #{hoveredSample.idx + 1}
            </div>
            <div className="flex justify-between gap-4 text-text-primary">
              <span className="text-text-muted">{t('braking.speed')}:</span>
              <span className="font-semibold text-emerald-400">{hoveredSample.speed.toFixed(1)} km/h</span>
            </div>
            <div className="flex justify-between gap-4 text-text-primary">
              <span className="text-text-muted">{t('braking.gLat')}:</span>
              <span className="font-semibold text-rose-400">
                {hoveredSample.gLat >= 0 ? '+' : ''}{hoveredSample.gLat.toFixed(2)} G ({hoveredSample.gLat < 0 ? 'L' : 'R'})
              </span>
            </div>
            <div className="flex justify-between gap-4 text-text-primary">
              <span className="text-text-muted">{t('braking.gLong')}:</span>
              <span className="font-semibold text-blue-400">
                {hoveredSample.gLong >= 0 ? '+' : ''}{hoveredSample.gLong.toFixed(2)} G ({hoveredSample.gLong < 0 ? t('braking.phaseBraking') : t('braking.phaseAccel')})
              </span>
            </div>
            <div className="flex justify-between gap-4 text-text-primary pt-1 border-t border-border-subtle">
              <span className="text-text-muted">{t('braking.gTotal')}:</span>
              <span className="font-bold text-amber-400">{hoveredSample.totalG.toFixed(2)} G</span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Phase Legend */}
      <div className="flex items-center justify-center gap-4 sm:gap-6 pt-3 border-t border-border-subtle w-full text-[11px] text-text-muted flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />
          <span>{t('braking.legendDecel')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />
          <span>{t('braking.legendTrail')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 inline-block" />
          <span>{t('braking.minSpeed')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
          <span>{t('braking.exit')}</span>
        </div>
        {comparisonLap && (
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-purple-500 inline-block" />
            <span>{comparisonLap.driverName} (L{comparisonLap.lapNumber})</span>
          </div>
        )}
      </div>
    </div>
  );
}
